<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <v-card>
      <v-card-title
        class="d-flex justify-space-between"
        data-cy="dialog-transcodeFile-title"
      >
        {{ $t('routes.pod.menuTools.manageVersions') }}
        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form
          ref="form"
          :disabled="!$canControlPodService()"
          v-on:submit.prevent="formSubmitted"
          data-cy="podMenu-tools-manageVersions-form"
        >
          <div class="mt-4">
            <v-text-field
              v-model="versionName"
              :disabled="false"
              :label="$t('routes.pod.menuVersions.versionName')"
              :rules="versionNameRules"
              variant="solo-filled"
              flat
              data-cy="podMenu-tools-manageVersions-name"
            />

            <v-select
              v-model="selectedPod"
              :label="$t('routes.pod.menuVersions.selectPod')"
              :rules="selectedPodRules"
              :items="availablePods"
              item-title="name"
              item-value="id"
              :menu-props="{ maxHeight: 320 }"
              :item-props="() => ({ tabindex: 0 })"
              variant="solo-filled"
              flat required return-object single-line
              data-cy="podMenu-tools-manageVersions-selectFile"
              :list-props="{ navigationStrategy: 'focus' }"
            />

            <div class="text-center">
              <v-btn
                type="submit"
                elevation=0 color="primary"
                :disabled="!$canControlPodService()"
                data-cy="podMenu-tools-manageVersions-submit-btn"
              >
                {{ $t('routes.pod.menuVersions.addVersion') }}
              </v-btn>
            </div>
          </div>

          <div v-if="errorMessage" class="bg-error" data-cy="dialog-exportSubtitle-fileTypeError">
            {{ errorMessage }}
          </div>

          <div class="bg-error" v-if="errorTranslatable">
            {{ $replaceT(errorTranslatable.code, errorTranslatable.params, errorTranslatable.content) }}
          </div>
        </v-form>

        <v-list density="compact" lines="two" class="mt-4">
          <v-list-subheader>
            {{ $t('routes.pod.menuVersions.versions') }}
          </v-list-subheader>

          <!-- TODO: Get real versions of the current pod here -->
          <draggable
            v-if="$canControlPodService()"
            v-model="draggableItems"
            v-on:end="reordered"
            item-key="id"
          >
            <template v-slot:item="{element}">
              <PodVersionItem :pod="element" v-on:removeItem="removeItem" />
            </template>
          </draggable>

          <div v-if="!$canControlPodService()">
            <PodVersionItem v-for="pod in draggableItems" :key="pod.id" :pod="pod" />
          </div>
        </v-list>


      </v-card-text>

      <v-card-actions>
        <v-spacer/>
        <v-btn variant="text" @click="dialog = false" data-cy="dialog-transcodeFile-false">
          {{ $t('common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import draggable from 'vuedraggable'
import PodVersionItem from "./PodVersionItem.vue"
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';

export default {
  mixins: [DialogActionKeys, DialogModel],
  name: "ManageVersionsDialog",
  components: {
    draggable,
    PodVersionItem,
  },
  data: function() {
    return {
      versionName: "",
      versionNameRules: [
        v => !!v || this.$t('routes.pod.menuVersions.versionNameRequired'),
      ],

      selectedPod: null,
      selectedPodRules: [
        v => !!v || this.$t('routes.pod.menuVersions.selectPodRequired'),
      ],

      draggableItems: [],
      successMessage: "",
      errorMessage: "",
      errorTranslatable: null,
    };
  },
  mounted: function() {
    this.draggableItems = this.getPodVersions || [];
  },
  watch: {
    getPodVersions: function(newPods) {
      this.draggableItems = newPods;
    },
    dialog: function(newDialog) {
      if (!newDialog) {
        this.resetForm();
      }
    }
  },
  methods: {
    resetForm: function () {
      if(this.$refs.form) {
        this.$refs.form.reset();
      }

      this.successMessage = "";
      this.errorMessage = "";
      this.errorTranslatable = null;

      this.draggableItems = this.getPodVersions || [];
    },
    formSubmitted: async function() {
      this.successMessage = "";
      this.errorMessage = "";
      this.errorTranslatable = null;
      const validateFormResult = await this.$refs.form.validate();
      if (!validateFormResult.valid) {
        return;
      }

      let payload = {
        sourcePodId: this.viewPod.id,
        targetPodId: this.selectedPod.id,
        versionText: this.versionName,
      };

      try {
        const podStore = usePodStore();
        podStore.addVersion(payload)

        this.$refs.form.reset();
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          const apiError = error.response.data.error.message;
          if (typeof apiError == 'object') {
            this.errorTranslatable = apiError;
          } else {
            this.errorMessage = apiError;
          }
        } else {
          this.errorMessage = "Error!";
          console.log(error);   // unhandled error
        }
      }
    },
    reordered: function() {
      this.removeItem(null);
    },
    removeItem: function(removeid) {
      if (this.draggableItems.length <= 1) {
        return;
      }

      if (removeid) {
        this.draggableItems = this.draggableItems.filter(x => x.id != removeid);
      }

      // check if there really is reordering, and return if there is no change
      let hasChanged = false;
      for (var i = 0; i < this.draggableItems.length; i++) {
        if (this.getPodVersions[i].id != this.draggableItems[i].id) {
          hasChanged = true;
          break;
        }
      }
      if (!hasChanged && !removeid) {
        return;
      }

      let payload = {
        podid: this.viewPod.id,
        podids: this.draggableItems.map(x => x.id),
        removeid: removeid || null,
      };

      const podStore = usePodStore();
      podStore.reorderVersions(payload)
      .catch(function(error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          const apiError = error.response.data.error.message;
          if (typeof apiError == 'object') {
            self.errorTranslatable = apiError;
          } else {
            self.errorMessage = apiError;
          }
        } else {
          self.errorMessage = "Error!";
          console.log(error);   // unhandled error
        }
      });
    },
  },
  computed: {
    viewPod: function() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
    childPods: function() {
      const podStore = usePodStore();
      return podStore.getPods;
    },
    availablePods: function() {
      // filter out all pods that have versions
      const pods = this.childPods.filter(x => x.parentpodid == null);
      return pods.filter((x) => {
        return (this.getPodVersionsMethod(x.id).length <= 1) && (x.id !== this.viewPod.id);
      });
    },

    getPodVersions: function() {
      if (!this.viewPod) {
        return [];
      }

      const podStore = usePodStore();
      return podStore.getPodVersions(this.viewPod.id);
    },
    getPodVersionsMethod: function() {
      const podStore = usePodStore();
      return podStore.getPodVersions;
    },
  }
}
</script>

<style>
</style>
